"use client";

import { useRef, useCallback, type KeyboardEvent } from "react";
import { Send, Square, ChevronDown } from "lucide-react";

export const MODELS = [
  { key: "deepseek", label: "DeepSeek V3" },
  { key: "claude", label: "Claude Sonnet 4.6" },
  { key: "haiku", label: "Claude Haiku 4.5" },
  { key: "gemini", label: "Gemini 3 Flash" },
  { key: "minimax", label: "MiniMax M2.7" },
  { key: "flash-lite", label: "Gemini 3.1 Flash Lite" },
  { key: "gpt5-nano", label: "GPT-5 Nano" },
  { key: "qwen-flash", label: "Qwen 3.5 Flash" },
  { key: "nemo", label: "Mistral Nemo" },
  { key: "mistral-small", label: "Mistral Small 3.2" },
  { key: "gemma3n", label: "Gemma 3n E4B" },
] as const;

export type ModelKey = (typeof MODELS)[number]["key"];

export const SPEED_OPTIONS = [
  { key: 0, label: "최대 속도" },
  { key: 50, label: "50ms" },
  { key: 100, label: "100ms" },
  { key: 200, label: "200ms" },
  { key: 300, label: "300ms" },
  { key: 500, label: "500ms" },
  { key: 1000, label: "1초 (기본)" },
] as const;

export type SpeedKey = (typeof SPEED_OPTIONS)[number]["key"];

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  model: ModelKey;
  onModelChange: (model: ModelKey) => void;
  showModelSelect?: boolean;
  speed?: SpeedKey;
  onSpeedChange?: (speed: SpeedKey) => void;
}

export function ChatInput({
  onSend,
  onStop,
  isStreaming,
  disabled,
  model,
  onModelChange,
  showModelSelect,
  speed,
  onSpeedChange,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  const handleSend = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const msg = el.value.trim();
    if (!msg) return;
    onSend(msg);
    el.value = "";
    el.style.height = "auto";
  }, [onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isStreaming && !disabled) {
          handleSend();
        }
      }
    },
    [isStreaming, disabled, handleSend],
  );

  return (
    <div className="border-t border-gray-100 bg-white px-4 pt-2 pb-3">
      {/* 모델 선택 (admin만) */}
      {showModelSelect && (
        <div className="mb-1.5 flex items-center gap-1.5 px-1">
          <span className="text-xs text-gray-400">엔진:</span>
          <div className="relative inline-flex">
            <select
              value={model}
              onChange={(e) => onModelChange(e.target.value as ModelKey)}
              disabled={isStreaming}
              className="cursor-pointer appearance-none rounded-md border border-gray-200 bg-white py-0.5 pr-6 pl-2 text-xs font-medium text-gray-600 transition-colors outline-none hover:border-violet-300 focus:border-violet-400 disabled:opacity-50"
            >
              {MODELS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute top-1/2 right-1 h-3 w-3 -translate-y-1/2 text-gray-400" />
          </div>
          {onSpeedChange && (
            <>
              <span className="ml-2 text-xs text-gray-400">속도:</span>
              <div className="relative inline-flex">
                <select
                  value={speed ?? 0}
                  onChange={(e) => onSpeedChange(Number(e.target.value) as SpeedKey)}
                  disabled={isStreaming}
                  className="cursor-pointer appearance-none rounded-md border border-gray-200 bg-white py-0.5 pr-6 pl-2 text-xs font-medium text-gray-600 transition-colors outline-none hover:border-violet-300 focus:border-violet-400 disabled:opacity-50"
                >
                  {SPEED_OPTIONS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute top-1/2 right-1 h-3 w-3 -translate-y-1/2 text-gray-400" />
              </div>
            </>
          )}
        </div>
      )}
      <div className="flex items-end gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 transition-colors focus-within:border-violet-300 focus-within:ring-1 focus-within:ring-violet-200">
        <textarea
          ref={textareaRef}
          rows={1}
          placeholder="궁금한 시술에 대해 물어보세요..."
          className="max-h-[120px] flex-1 resize-none bg-transparent text-sm leading-relaxed text-gray-800 outline-none placeholder:text-gray-400"
          onInput={adjustHeight}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600"
            aria-label="중지"
          >
            <Square className="h-3.5 w-3.5" fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="전송"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <p className="mt-1.5 text-center text-xs text-gray-300">
        PubMed 논문 기반 정보 제공 · 의료적 조언이 아닙니다
      </p>
    </div>
  );
}
